import ButtonsGroup from "@components/ButtonsGroup";
import Dialog from "@components/Dialog";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import Text from "@components/Text";
import styled from "@emotion/styled";
import { Column } from "@src/components/Flex";
import Scrollbar from "@src/components/Scrollbar";
import Balance from "@src/entities/Balance";
import useOnClickOutside from "@src/hooks/useOnClickOutside";
import { useStores } from "@stores";
import { observer } from "mobx-react-lite";
import React, { useRef, useState } from "react";
import TokenInfo from "./TokenInfo";

interface IProps {
  onClose: () => void;
  balances: Balance[];
  onSelect: (assetId: string) => void;
  visible: boolean;
  selectedTokenId?: string;
}

const tokenCategories = [
  "All",
  "Favorites",
  // "Global",
  "Stables",
  "Common",
  "PZ LP",
  "Memes",
  // "Waves DeFi",
  // "Waves Ducks",
  "Hidden"
];

export enum tokenCategoriesEnum {
  all = 0,
  favorites = 1,
  // global = 2,
  // stable = 3,
  // ducks = 4,
  stable = 2,
  common = 3,
  pz = 4,
  // defi = 5,
  meme = 5,
  hidden = 6
}

const Scroll = styled.div`
  display: flex;
  margin: 0 -24px;
  padding: 0 40px 16px 24px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  cursor: grab;
  user-select: none;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &:active {
    cursor: grabbing;
  }

  ::-webkit-scrollbar {
    display: none;
  }

  border-bottom: 1px solid ${({ theme }) => theme.colors.primary100};
`;
const TokenSelectModal: React.FC<IProps> = ({ onClose, balances, onSelect, visible, selectedTokenId }) => {
  const { tokenStore } = useStores();
  const [searchValue, setSearchValue] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<number>(0);
  const handleSearch = (event: any) => {
    setSearchValue(event.target.value);
  };
  const ref = useRef(null!);
  useOnClickOutside(ref, onClose);

  const scrollRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ down: false, moved: false, startX: 0, startScroll: 0 });

  const handleScrollPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    dragState.current = { down: true, moved: false, startX: e.clientX, startScroll: el.scrollLeft };
  };

  const handleScrollPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !dragState.current.down) return;
    const dx = e.clientX - dragState.current.startX;
    if (Math.abs(dx) > 3) {
      dragState.current.moved = true;
      el.setPointerCapture(e.pointerId);
    }
    el.scrollLeft = dragState.current.startScroll - dx;
  };

  const handleScrollPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (el?.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    dragState.current.down = false;
  };

  // Block click on category if it was a drag, not a tap
  const handleScrollClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragState.current.moved) {
      e.stopPropagation();
      e.preventDefault();
      dragState.current.moved = false;
    }
  };

  // Convert vertical wheel into horizontal scroll
  const handleScrollWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      el.scrollLeft += e.deltaY;
    }
  };

  const handleTokenSelect = (assetId: string) => {
    onSelect(assetId);
    setActiveFilter(0);
    onClose();
  };

  const handleFavoriteToggle = (assetId: string) => {
    if (tokenStore.watchList.includes(assetId)) {
      tokenStore.removeFromWatchList(assetId);
    } else {
      tokenStore.addToWatchList(assetId);
    }
  };

  const handleHideToggle = (assetId: string) => {
    if (tokenStore.isTokenHidden(assetId)) {
      tokenStore.unhideToken(assetId);
    } else {
      tokenStore.hideToken(assetId);
    }
  };

  const filteredTokens = balances
    .filter((v) => {
      if (!v || !v.symbol || !v.name) {
        return false;
      }
      return (
        v.symbol.toLowerCase().includes(searchValue.toLowerCase()) ||
        v.name.toLowerCase().includes(searchValue.toLowerCase())
      );
    })
    .filter((balance) => {
      // If viewing hidden category, show only hidden tokens
      if (activeFilter === tokenCategoriesEnum.hidden) {
        return tokenStore.isTokenHidden(balance.assetId);
      }
      // Otherwise, hide hidden tokens from all other categories
      if (tokenStore.isTokenHidden(balance.assetId)) {
        return false;
      }
      if (activeFilter === 0) return true;
      if (activeFilter === tokenCategoriesEnum.favorites) {
        return tokenStore.watchList.includes(balance.assetId);
      }
      return balance.category?.includes(tokenCategoriesEnum[activeFilter]);
    });

  // Sort: favorites first, then by balance
  const sortedTokens = [...filteredTokens].sort((a, b) => {
    const aIsFav = tokenStore.watchList.includes(a.assetId);
    const bIsFav = tokenStore.watchList.includes(b.assetId);
    if (aIsFav && !bIsFav) return -1;
    if (!aIsFav && bIsFav) return 1;
    return 0;
  });

  return (
    <Dialog
      visible={visible}
      style={{ maxWidth: 360 }}
      styles={{
        body: { minHeight: 440 }
      }}
      onClose={() => {
        setActiveFilter(0);
        onClose();
      }}
      title="Select a token"
    >
      <Input icon="search" value={searchValue} onChange={handleSearch} placeholder="Search by name or ticker…" />
      <SizedBox height={16} />

      <Scroll
        ref={scrollRef}
        onPointerDown={handleScrollPointerDown}
        onPointerMove={handleScrollPointerMove}
        onPointerUp={handleScrollPointerUp}
        onPointerLeave={handleScrollPointerUp}
        onClickCapture={handleScrollClickCapture}
        onWheel={handleScrollWheel}
      >
        <ButtonsGroup values={tokenCategories} active={activeFilter} onClick={(v) => setActiveFilter(v)} />
      </Scroll>
      <SizedBox height={32} />
      <Scrollbar style={{ margin: -24 }}>
        <Column crossAxisSize="max" style={{ maxHeight: 352 }}>
          {sortedTokens && sortedTokens.length > 0 ? (
            sortedTokens.map((t) => {
              const disabled = selectedTokenId === t.assetId;
              return (
                <TokenInfo
                  hidden={disabled}
                  style={{ position: "relative" }}
                  withClickLogic
                  onClick={!disabled ? () => handleTokenSelect(t.assetId) : () => null}
                  key={t.assetId}
                  token={t}
                  isFavorite={tokenStore.watchList.includes(t.assetId)}
                  onFavoriteToggle={handleFavoriteToggle}
                  isHidden={tokenStore.isTokenHidden(t.assetId)}
                  onHideToggle={handleHideToggle}
                />
              );
            })
          ) : (
            <Text style={{ padding: "10px 24px" }}>No tokens found</Text>
          )}
          <SizedBox height={32} width={16} />
        </Column>
      </Scrollbar>
    </Dialog>
  );
};
export default observer(TokenSelectModal);
